import { useMemo } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { Slider } from '../ui/slider';
import { Badge } from '../ui/badge';
import { cn } from '../../lib/utils';
import {
  endOfWeekYmd,
  formatWeekSpanLabel,
  mondayOfWeekYmd,
  weekIndexForYmd,
} from '../../utils/blockCalendarWeekUtils';

const KIND_BADGE = {
  period: 'bg-slate-600 text-white hover:bg-slate-600',
  fixed_weeks: 'bg-emerald-600 text-white hover:bg-emerald-600',
  target_race: 'bg-rose-600 text-white hover:bg-rose-600',
};

const ACTIVE_STATUSES = new Set(['draft', 'active']);

function blockWeekSpan(block, boundsWeeks) {
  const startIdx = weekIndexForYmd(block.start_date, boundsWeeks);
  const endIdx = weekIndexForYmd(block.end_date, boundsWeeks);
  return Math.max(1, endIdx - startIdx + 1);
}

function blockWeekSpanLabel(block) {
  const weekStart = mondayOfWeekYmd(block.start_date);
  const weekEnd = endOfWeekYmd(mondayOfWeekYmd(block.end_date));
  return formatWeekSpanLabel(weekStart, weekEnd);
}

export default function BlockCalendarRangeNav({
  boundsWeeks,
  catalogBlocks,
  rangeIdx,
  onRangeChange,
  onRangeCommit,
  focusBlockId,
  onBlockFocus,
}) {
  const { t } = useLanguage();

  const maxIdx = Math.max(0, (boundsWeeks?.length || 1) - 1);
  const safeRange = rangeIdx || [0, maxIdx];

  const viewFrom = boundsWeeks?.[safeRange[0]] || boundsWeeks?.[0];
  const viewToWeekStart = boundsWeeks?.[safeRange[1]] || boundsWeeks?.[maxIdx];
  const viewTo = viewToWeekStart ? endOfWeekYmd(viewToWeekStart) : viewToWeekStart;

  const activeBlocks = useMemo(() => {
    const blocks = (catalogBlocks || []).filter((b) => ACTIVE_STATUSES.has(b.status));
    return [...blocks].sort(
      (a, b) => blockWeekSpan(b, boundsWeeks) - blockWeekSpan(a, boundsWeeks)
    );
  }, [catalogBlocks, boundsWeeks]);

  if (!boundsWeeks?.length) return null;

  return (
    <div className='space-y-3 rounded-lg border border-border bg-muted/20 p-3'>
      <div className='flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground'>
        <span>{t('periodization.calendar.rangeLabel')}</span>
        <span className='font-medium tabular-nums text-foreground'>
          {viewFrom && viewTo ? formatWeekSpanLabel(viewFrom, viewTo) : '—'}
        </span>
      </div>

      <Slider
        min={0}
        max={maxIdx}
        step={1}
        minStepsBetweenThumbs={1}
        value={safeRange}
        onValueChange={(vals) => {
          const a = Math.min(vals[0], vals[1]);
          const b = Math.max(vals[0], vals[1]);
          onRangeChange([a, b]);
        }}
        onValueCommit={(vals) => {
          const a = Math.min(vals[0], vals[1]);
          const b = Math.max(vals[0], vals[1]);
          onRangeCommit?.([a, b]);
        }}
        aria-label={t('periodization.calendar.rangeSliderAria')}
      />

      {activeBlocks.length > 0 && (
        <div className='flex flex-wrap gap-1.5'>
          {activeBlocks.map((block) => {
            const isFocused = focusBlockId && String(block.id) === String(focusBlockId);
            const badgeClass = KIND_BADGE[block.block_kind] || KIND_BADGE.fixed_weeks;
            const spanLabel = blockWeekSpanLabel(block);
            const title = `${block.name} · ${spanLabel}`;

            return (
              <button
                key={block.id}
                type='button'
                onClick={() => onBlockFocus?.(block)}
                className='inline-flex max-w-full rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
                title={title}
                aria-label={t('periodization.calendar.focusBlockAria', { name: block.name, span: spanLabel })}
              >
                <Badge
                  variant='secondary'
                  className={cn(
                    'max-w-full gap-1 px-2 py-0.5 text-[10px] font-medium shadow-sm',
                    badgeClass,
                    isFocused && 'ring-2 ring-amber-400 ring-offset-1 ring-offset-background'
                  )}
                >
                  <span className='truncate'>{block.name}</span>
                  <span className='shrink-0 opacity-90'>· {spanLabel}</span>
                </Badge>
              </button>
            );
          })}
        </div>
      )}

      <div className='flex justify-between text-[10px] tabular-nums text-muted-foreground'>
        <span>{boundsWeeks[0]?.slice(5)}</span>
        <span>{boundsWeeks[maxIdx]?.slice(5)}</span>
      </div>
    </div>
  );
}
